import React, {useState, useEffect} from "react";
import {useSelector, useDispatch} from "react-redux";
import useOnClickOutside from "use-onclickoutside";
import {isPast, parse} from "date-fns";

import * as actionTypes from "../../store/actions/actionTypes";
import * as modalActions from "../../store/actions/modal";
import * as admissionListActions from "../../store/actions/admissionList";
import ModalSelectDocumentSet_Actions from "./ModalSelectDocumentSet_Actions";
import ModalOecStatus_Claim from "./ModalOecStatus_Claim";
import ModalOecStatus_Claim_NOI from "./ModalOecStatus_Claim_NOI";
import CollapsableString from "../CollapsableString";
import FundAssessmentCode from "../FundAssessmentCode";
import {setPresentingIllnessCodes} from "../../store/actions/modal";
import {presentingIllnessCodeDescription} from "../../utils/presentingIllnessCodes";
import AdmissionDocumentPdf from "../AdmissionDocumentPdf";
import UrDt from "../CustomLogic/UrDt";
import AdmDt from "../CustomLogic/AdmDt";
import {useCustomLogic} from "../../utils/customLogic";
import OecClaim from "../OecClaim/OecClaim";

const ModalOecStatus_Proda = () => {
    const {show, admission, prodaRequests} = useSelector(state => state.modal.oecStatus_proda);
    const manager_noMbsUi = useSelector(state => state.auth.manager_noMbsUi);
    const dispatch = useDispatch();
    const ref = React.useRef(null);
    useOnClickOutside(ref, () => hideModal());
    const [visibleClaim, setVisibleClaim] = useState(false);
    const {isCustomLogicLifehouse} = useCustomLogic();
    const [refreshDisabledUntil, setRefreshDisabledUntil] = useState(0);
    const [now, setNow] = useState(Date.now());

    // refresh button is rate-limited to once per minute, tick every second while it's on cooldown
    useEffect(() => {
        if (refreshDisabledUntil > now) {
            const timer = setTimeout(() => setNow(Date.now()), 1000);
            return () => clearTimeout(timer);
        }
    }, [now, refreshDisabledUntil]);

    const refreshOecStatus = () => {
        if (now < refreshDisabledUntil) {
            return;
        }
        dispatch(modalActions.showOecForAdmission(admission));
        setRefreshDisabledUntil(Date.now() + 60000);
        setNow(Date.now());
    }

    const hideModal = () => {
        dispatch({type: actionTypes.MODAL_OEC_STATUS_PRODA_HIDE});
        // Modal can change admissions status, so list need to be reloaded
        // dispatch doesnt work for admission page
        // dispatch(admissionListActions.load());
        // reload dont loose filter settings (hospital / date) any more
        // window.location.reload();
    }

    useEffect(() => {
        if (prodaRequests.length > 0) {
            setVisibleClaim(prodaRequests[0]);
        } else {
            setVisibleClaim(false);
        }
    }, [prodaRequests]);

    const renderClaimTitle = claim => {
        switch (claim.statusConst) {
            case "STATUS_SCHEDULED":
                return (
                    <>
                        <dt><_DevUI_IdLabel claim={claim}/><span className="badge badge-warning">Scheduled</span> at {claim.createdAt}</dt>
                        {/*<dd><FundAssessmentCode fundAssessmentCode={claim.statusStr} /> </dd>*/}
                    </>
                );
            case "STATUS_RUNNING":
                return (
                    <>
                        <dt><_DevUI_IdLabel claim={claim}/><span className="badge badge-warning">Processing</span> at {claim.createdAt}</dt>
                        {/*<dd><FundAssessmentCode fundAssessmentCode={claim.statusStr} /> </dd>*/}
                    </>
                );
            case "STATUS_FAILED":
            case "STATUS_ERROR":
                return (
                    <>
                        <dt><_DevUI_IdLabel claim={claim}/><span className="badge badge-danger">ERROR</span> at {claim.createdAt}</dt>
                        {/*<dd><FundAssessmentCode fundAssessmentCode={claim.statusStr} /> </dd>*/}
                    </>
                );
            case "STATUS_COMPLETED":
            case "STATUS_DONE_ACCEPTED":
            case "STATUS_DONE_REJECTED":
            case "STATUS_DONE_WARNING":
                return (
                    <>
                        <dt><_DevUI_IdLabel claim={claim}/><span className="badge badge-success">Done</span> at {claim.completedAt}</dt>
                        {/*<dd><FundAssessmentCode fundAssessmentCode={claim.statusStr} /> </dd>*/}
                    </>
                );
        }
    }

    const renderOecSelectors = () => {
        return prodaRequests.map((claim, index) => (
            <li className={`oec-selector${visibleClaim && (visibleClaim.id === claim.id) ? ' oec-selector-selected' : ''}`} onClick={() => setVisibleClaim(claim)} key={index} >
                <dl className="person-list">
                    {renderClaimTitle(claim)}
                    {claim.mbsCode && <>
                        <dt>{claim.mbsCode}</dt>
                        <dd><CollapsableString str={claim.mbsCode_description} length={150} /></dd>
                    </>}
                    {claim.illnessCode && <>
                        <dd>{presentingIllnessCodeDescription(claim.illnessCode)}</dd>
                    </>}
                </dl>
            </li>
        ));
    }

    const renderClaim = () => {
        if (visibleClaim) {
            // return <ModalOecStatus_Claim_NOI claim={visibleClaim} admission={admission} />
            return <OecClaim claim={visibleClaim} admission={admission} />
        } else if (prodaRequests.length ===0 ) {
            return <div className="col-9">
                <div className="main-content">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="h4">OEC claims not found</h2>
                        </div>
                    </div>
                </div>
            </div>
        } else {
            return <div className="col-9">
                <div className="main-content">
                    <div className="row">
                        <div className="col-12">
                            <h2 className="h4">Please select claim</h2>
                        </div>
                    </div>
                </div>
            </div>
        }
    }

    const sendToQueue = () => {
        dispatch({type: actionTypes.MODAL_OEC_STATUS_HIDE})
        dispatch(modalActions.sendToQueue(admission))
    }

    const reRunOEC = () => {
        dispatch({type: actionTypes.MODAL_OEC_STATUS_HIDE})
        dispatch(modalActions.reRunOec(admission))
        setRefreshDisabledUntil(Date.now() + 60000);
        setNow(Date.now());
    }

    const codesCountStr = () => {
        if (admission.mbsCodes_count) {
            return `${admission.mbsCodes_count} MBS codes`;
        } else if (admission.illCodes_count) {
            return `${admission.illCodes_count} Presenting Illness Codes`;
        } else {
            return `0 MBS codes`;
        }
    }

    const codesInfo = () => {
        if (admission.mbsCodes_count) {
            return <>
                    <dt>MBS codes</dt>
                    <dd>{admission.mbsCodes_str}</dd>
                </>;
        } else if (admission.illCodes_count) {
            return <>
                <dt>Presenting Illness Codes</dt>
                <dd>{admission.illCodes_str} <a href="#" onClick={setPresentingIllnessCodes}>edit</a></dd>
            </>;
        } else {
            return null;
        }
    }

    const setPresentingIllnessCodes = event => {
        event.preventDefault();
        if (manager_noMbsUi) {
            dispatch({type: actionTypes.MODAL_OEC_STATUS_HIDE});
            dispatch({type: actionTypes.MODAL_PRESENTING_ILLNESS_CODES_SHOW, admission: admission});
        }
    }

    const renderButtons = () => {
        if (admission.statusOECStr === 'STATUS_OEC_MODERATION') {
            return <>
                <p>OEC Auto check is not enabled for hospital {admission.hospitalTitle}</p>
                <button className="btn btn-sm btn-success" onClick={sendToQueue}>Send to OEC Queue</button>
                </>
        } else {
            const _date = parse(admission.admissionDate, 'dd/MM/yyyy', new Date())
            _date.setHours(23,59,59);
            // console.log('[isPast]', , admission.admissionDate, _date);
            const oec_pastAdmissionsManualCheck = admission.hospital && admission.hospital.oec_pastAdmissionsManualCheck;
            const reRunDisabled = isPast(_date) && !oec_pastAdmissionsManualCheck;
            return <button className="btn btn-sm btn-warning" disabled={reRunDisabled} onClick={reRunOEC}>Re-run {codesCountStr()}</button>
        }
    }
    // const leftSidebarClassname = isCustomLogicLifehouse() ? "col-2" : "col-3";
    // const claimClassname = isCustomLogicLifehouse() ? "col-5" : "col-9";
    const leftSidebarClassname =  "col-3";
    const claimClassname = "col-9";

    const renderModal = () => {
        return (
            <div className="modal-dialog modal-wide height-90" role="document" ref={ref}>
                <div className="modal-content height-100">
                    <button type="button" className="close" data-dismiss="modal" aria-label="Close" onClick={hideModal}>
                        <span aria-hidden="true">×</span>
                    </button>
                    <div className="row">
                        <div className="col-12">
                            <div className="heading">
                                <h1 className="h2"><span>OEC Claims for {admission.title} {admission.surname}, {admission.name}</span></h1>
                            </div>
                        </div>
                    </div>
                    <div className="row">
                        <div className={leftSidebarClassname}>
                            <aside className="sidebar bg-white rounded shadow-sm border">
                                <ul className="person-info-list">
                                    <li>
                                        <dl className="person-list">
                                            <UrDt admission={admission}/>
                                            <AdmDt admission={admission}/>
                                            <dt>Date</dt>
                                            <dd>{admission.admissionDate} {admission.admissionTime}</dd>

                                        </dl>
                                    </li>
                                    <li>
                                        <dl className="person-list" style={{marginBottom: '10px'}}>
                                            <dt>Fund</dt>
                                            <dd>{admission.fundCompleteTitle}</dd>
                                            <dt>Membership Num</dt>
                                            <dd>{admission.fundMemberNo}</dd>
                                            {admission.adm2_medicareNo && <>
                                                <dt>Medicare Num</dt>
                                                <dd>{admission.adm2_medicareNo}</dd>
                                            </>}
                                            {admission.adm2_medicareRefNo && <>
                                                <dt>Medicare Ref Num</dt>
                                                <dd>{admission.adm2_medicareRefNo}</dd>
                                                </>}
                                            {codesInfo()}
                                        </dl>
                                        {renderButtons()}
                                        {' '}
                                        <button className="btn btn-sm btn-secondary" onClick={refreshOecStatus} disabled={now < refreshDisabledUntil}>
                                            {now < refreshDisabledUntil
                                                ? `Refresh in ${Math.ceil((refreshDisabledUntil - now) / 1000)}s`
                                                : 'Refresh status'}
                                        </button>
                                    </li>
                                    {renderOecSelectors()}
                                </ul>
                            </aside>
                        </div>
                        {renderClaim()}
                        {visibleClaim && isCustomLogicLifehouse()
                            && <div className={claimClassname}>
                                <div className="main-content">
                                    <div className="row">
                                        <div className="col-12">
                                            <AdmissionDocumentPdf admissionId={admission.id} type="ifc" width="100%"
                                                                  height="750px"/>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            </div>
        );
    }

    return (
        <>
            <div className={`modal fade ${show ? 'show' : ''}`} id="personModal" tabIndex="-1" role="dialog"
                 aria-labelledby="personModalLabel" aria-modal="true">
                {show && renderModal()}
            </div>
        </>
    );
}

const _DevUI_IdLabel = ({claim}) => {
    const state_showDevUI = useSelector(state => state.auth.state_showDevUI);
    if (!state_showDevUI) {
        return;
    }

    return <>[ #{claim.id} ]&nbsp;</>
}

export default ModalOecStatus_Proda;